/**
 * StaffLivret — Upload / replace the official BBr presentation booklet (livret) PDF.
 * Accessible under /staff/livret (manager/admin only).
 */
import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, getStaffToken } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const fmtSize = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`);

export default function StaffLivret() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/livret/pdf");
      setInfo(r.data);
    } catch {
      setInfo({ available: false });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Le fichier doit être un PDF.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = getStaffToken();
      const r = await fetch(`${API}/staff/livret/pdf`, {
        method: "POST",
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Upload échoué (${r.status})`);
      }
      toast.success("Livret mis à jour");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Upload impossible");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleDelete() {
    if (!window.confirm("Retirer le livret actuel du site public ?")) return;
    try {
      await api.delete("/staff/livret/pdf");
      toast.success("Livret retiré");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
  }

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-4xl mx-auto" data-testid="staff-livret">
      <div className="mb-8">
        <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A]">Livret BBr</h1>
        <p className="text-sm text-[#0A0A0A]/55 mt-2">
          Importez la brochure officielle de présentation du resort au format PDF.
          Le fichier sera téléchargeable depuis le menu mobile via l&apos;entrée
          « Télécharger notre livret ».
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-[#0A0A0A]/45 py-10">Chargement…</div>
      ) : (
        <>
          {/* Current file state */}
          <div className="bg-white border border-[#0A0A0A]/10 p-6 md:p-8 mb-6">
            <div className="text-[0.62rem] uppercase tracking-[0.35em] text-[#B8922A] mb-4">
              État actuel
            </div>
            {info?.available ? (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#B8922A]/10 text-[#B8922A] flex items-center justify-center flex-shrink-0">
                  <FileText size={22} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display-serif text-lg text-[#0A0A0A] break-all">
                    {info.filename || "livret.pdf"}
                  </div>
                  <div className="text-xs text-[#0A0A0A]/55 mt-1">
                    {info.size ? fmtSize(info.size) : ""}{info.uploaded_at ? ` · Importé le ${new Date(info.uploaded_at).toLocaleString("fr-FR")}` : ""}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <a
                      href={`${BACKEND_URL}${info.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-[#0A0A0A] border-b border-[#0A0A0A] pb-0.5 hover:text-[#B8922A] hover:border-[#B8922A]"
                      data-testid="staff-livret-view"
                    >
                      <ExternalLink size={12} strokeWidth={1.5} />
                      Ouvrir le PDF
                    </a>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-red-600 hover:text-red-800"
                      data-testid="staff-livret-delete"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                      Retirer du site
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#0A0A0A]/60">
                Aucun livret n&apos;est actuellement publié sur le site.
              </div>
            )}
          </div>

          {/* Upload zone */}
          <div className="bg-white border border-dashed border-[#0A0A0A]/25 p-8 text-center">
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFile}
              className="hidden"
              data-testid="staff-livret-file-input"
            />
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#B8922A]/10 text-[#B8922A] mb-4">
              <Upload size={22} strokeWidth={1.5} />
            </div>
            <p className="text-base text-[#0A0A0A] font-serif font-light mb-2">
              {info?.available ? "Remplacer le livret actuel" : "Publier un nouveau livret"}
            </p>
            <p className="text-xs text-[#0A0A0A]/50 mb-6">
              Formats acceptés : PDF · Taille max : 30 Mo
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#B8922A] text-white px-7 py-3 text-[0.7rem] tracking-[0.32em] uppercase transition-colors disabled:opacity-50"
              data-testid="staff-livret-upload-btn"
            >
              {uploading ? (
                <><Loader2 size={13} className="animate-spin" strokeWidth={1.5} /> Envoi…</>
              ) : (
                <><Upload size={13} strokeWidth={1.5} /> Sélectionner un PDF</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
